import { useQuery } from '@tanstack/react-query';
import { produce } from 'immer';
import { type ContextType, createContext, type ReactNode, useEffect, useMemo, useState } from 'react';
import { useCookie } from 'react-use';

import useMediaLibrarySettings from '~modules/media-library/context/useMediaLibrarySettings';
import { queries } from '~modules/media-library/controller/api/Queries';
import { type Filter } from '~modules/media-library/model/Filter';
import { DEFAULT_SORT_KEY } from '~modules/media-library/model/SortOption';
import { SortOrder } from '~modules/media-library/model/SortOrder';
import { ViewContext } from '~modules/media-library/model/ViewContext';
import { ViewVariant } from '~modules/media-library/model/ViewVariant';
import { mergeDeep } from '~services/immutable';
import { removeUndefined } from '~utils/object';

type ContextStateShape = {
  activeFilterIds: Filter['id'][];
  selectedFilterValueIds: string[];
  selectedTabFilterId: string;
  sortKey: string;
  preferredSortKey: string;
  sortOrder: SortOrder;
  viewVariant: ViewVariant;
  showFavouritesOnly: boolean;
  preferencesChanged: boolean;
};

export const MediaLibraryUserPreferencesContext = createContext<
  | (ContextStateShape & {
      onSetActiveFilterIds: (_filterIds: Filter['id'][]) => void;
      onSetSelectedFilterValueIds: (_valueIds: string[]) => void;
      onSetSelectedTabFilterId: (_id: string) => void;
      onSort: (_sortKey: string, _sortOrder: SortOrder) => void;
      onChangeViewVariant: (_viewVariant: ViewVariant) => void;
      toggleShowFavouritesOnly: () => void;
    })
  | undefined
>(undefined);

export function MediaLibraryUserPreferencesProvider({ children }: { children: ReactNode }) {
  const [cookieValue, updateCookie] = useCookie('ebm_ml_pref');
  // read settings
  const { realm, idCollection, viewContext } = useMediaLibrarySettings();

  const DEFAULT_KEY = 'DEFAULT' + realm;
  const PAGE_KEY = viewContext !== ViewContext.DEFAULT ? viewContext : null;

  // prepare state for context
  const [contextState, setContextState] = useState<Record<string, ContextStateShape>>(() => {
    const defaultState = {
      [DEFAULT_KEY]: {
        activeFilterIds: [],
        selectedFilterValueIds: [],
        selectedTabFilterId: '',
        sortKey: '',
        preferredSortKey: '',
        sortOrder: SortOrder.ASC,
        viewVariant: ViewVariant.DEFAULT,
        showFavouritesOnly: false,
        preferencesChanged: false,
      },
      [ViewContext.FAVOURITE]: {
        activeFilterIds: [],
        selectedFilterValueIds: [],
        selectedTabFilterId: '',
        sortKey: '',
        preferredSortKey: '',
        sortOrder: SortOrder.ASC,
        viewVariant: ViewVariant.DEFAULT,
        showFavouritesOnly: false,
        preferencesChanged: false,
      },
      [ViewContext.SEARCH]: {
        activeFilterIds: [],
        selectedFilterValueIds: [],
        selectedTabFilterId: '',
        sortKey: '',
        preferredSortKey: '',
        sortOrder: SortOrder.ASC,
        viewVariant: ViewVariant.DEFAULT,
        showFavouritesOnly: false,
        preferencesChanged: false,
      },
    };

    // read from cookie
    try {
      const parsedCookieValue = JSON.parse(cookieValue);
      return mergeDeep(defaultState, parsedCookieValue);
    } catch (_) {
      // Do nothing
      return defaultState;
    }
  });

  const defaultCollectionState = {
    activeFilterIds: [],
    selectedFilterValueIds: [],
    selectedTabFilterId: '',
    sortKey: '',
    preferredSortKey: '',
    sortOrder: SortOrder.ASC,
    viewVariant: ViewVariant.DEFAULT,
    showFavouritesOnly: false,
    preferencesChanged: false,
  };

  // Effect for storing the state in a cookie
  useEffect(() => {
    updateCookie(JSON.stringify(contextState));
  }, [contextState, updateCookie]);

  // Handler
  const handler = useMemo(
    () => ({
      onSetActiveFilterIds: (ids: Filter['id'][] = []) =>
        setContextState(state =>
          produce(state, draft => {
            draft[idCollection || PAGE_KEY || DEFAULT_KEY].activeFilterIds = ids;
          }),
        ),
      onSetSelectedFilterValueIds: (ids: string[] = []) =>
        setContextState(state =>
          produce(state, draft => {
            draft[idCollection || PAGE_KEY || DEFAULT_KEY].selectedFilterValueIds = ids;
            draft[idCollection || PAGE_KEY || DEFAULT_KEY].preferencesChanged = true;
          }),
        ),
      onSetSelectedTabFilterId: (id: string = '') =>
        setContextState(state =>
          produce(state, draft => {
            draft[idCollection || PAGE_KEY || DEFAULT_KEY].selectedTabFilterId = id;
            draft[idCollection || PAGE_KEY || DEFAULT_KEY].preferencesChanged = true;
          }),
        ),
      onSort: (sortKey: string, sortOrder: SortOrder) =>
        setContextState(state =>
          produce(state, draft => {
            draft[idCollection || PAGE_KEY || DEFAULT_KEY].sortKey = sortKey;
            if (sortKey !== DEFAULT_SORT_KEY) draft[idCollection || PAGE_KEY || DEFAULT_KEY].preferredSortKey = sortKey;
            draft[idCollection || PAGE_KEY || DEFAULT_KEY].sortOrder = sortOrder;
            draft[idCollection || PAGE_KEY || DEFAULT_KEY].preferencesChanged = true;
          }),
        ),
      onChangeViewVariant: (viewVariant: ViewVariant) =>
        setContextState(state =>
          produce(state, draft => {
            draft[idCollection || PAGE_KEY || DEFAULT_KEY].viewVariant = viewVariant;
            draft[idCollection || PAGE_KEY || DEFAULT_KEY].preferencesChanged = true;
          }),
        ),
      toggleShowFavouritesOnly: () =>
        setContextState(state =>
          produce(state, draft => {
            draft[idCollection || PAGE_KEY || DEFAULT_KEY].showFavouritesOnly =
              !state[idCollection || PAGE_KEY || DEFAULT_KEY].showFavouritesOnly;
            draft[idCollection || PAGE_KEY || DEFAULT_KEY].preferencesChanged = true;
          }),
        ),
    }),
    [DEFAULT_KEY, idCollection, PAGE_KEY],
  );

  // query settings where we extract the preferences from
  const { data } = useQuery({
    ...queries.mediaLibrary.settings(realm, idCollection),
    retry: false,
    gcTime: Infinity,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (idCollection === undefined) return;

    setContextState(currentContextState => ({
      [idCollection]: defaultCollectionState,
      ...currentContextState,
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCollection]);

  useEffect(() => {
    if (data === undefined) return;
    const newContext: Partial<ContextStateShape> = removeUndefined({
      ...data?.preferences,
    });
    setContextState(currentContextState => ({
      ...currentContextState,
      [idCollection || PAGE_KEY || DEFAULT_KEY]: {
        ...newContext,
        ...currentContextState[idCollection || PAGE_KEY || DEFAULT_KEY],
      },
    }));

    // reset default state for preferencesChanged flag
    if (
      contextState[idCollection || PAGE_KEY || DEFAULT_KEY].activeFilterIds.length === 0 &&
      Boolean(newContext.activeFilterIds)
    ) {
      setContextState(currentContextState => ({
        ...currentContextState,
        [idCollection || PAGE_KEY || DEFAULT_KEY]: {
          ...currentContextState[idCollection || PAGE_KEY || DEFAULT_KEY],
          ...{
            activeFilterIds: newContext.activeFilterIds,
          },
        },
      }));
    }

    if (
      !Boolean(contextState[idCollection || PAGE_KEY || DEFAULT_KEY].selectedTabFilterId) &&
      Boolean(newContext.selectedTabFilterId)
    ) {
      setContextState(currentContextState => ({
        ...currentContextState,
        [idCollection || PAGE_KEY || DEFAULT_KEY]: {
          ...currentContextState[idCollection || PAGE_KEY || DEFAULT_KEY],
          ...{
            selectedTabFilterId: newContext.selectedTabFilterId,
          },
        },
      }));
    }

    if (
      contextState[idCollection || PAGE_KEY || DEFAULT_KEY].sortKey === '' &&
      Boolean(newContext.sortKey) &&
      Boolean(newContext.sortOrder)
    ) {
      setContextState(currentContextState => ({
        ...currentContextState,
        [idCollection || PAGE_KEY || DEFAULT_KEY]: {
          ...currentContextState[idCollection || PAGE_KEY || DEFAULT_KEY],
          ...{
            sortKey: newContext.sortKey,
            preferredSortKey: newContext.sortKey,
            sortOrder: newContext.sortOrder,
          },
        },
      }));
    }

    // reset default state for preferencesChanged flag
    setContextState(currentContextState => ({
      ...currentContextState,
      [idCollection || PAGE_KEY || DEFAULT_KEY]: {
        ...currentContextState[idCollection || PAGE_KEY || DEFAULT_KEY],
        ...{
          preferencesChanged: false,
        },
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // combine context value
  const value: ContextType<typeof MediaLibraryUserPreferencesContext> = useMemo(
    () => ({
      ...contextState[idCollection || PAGE_KEY || DEFAULT_KEY],
      ...handler,
    }),
    [DEFAULT_KEY, contextState, handler, idCollection, PAGE_KEY],
  );

  return (
    <MediaLibraryUserPreferencesContext.Provider value={value}>{children}</MediaLibraryUserPreferencesContext.Provider>
  );
}
